// JSON-ish syntax colouring for request bodies (design §3.5 code block).
// Tolerates partial JSON while the body is still streaming.

const TOKEN =
  /("(?:[^"\\\n]|\\.)*"?)(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b|([[\]{}])|([,:])/g

export function highlightJson(source) {
  const text = String(source ?? '')
  const out = []
  let last = 0
  let n = 0
  for (const match of text.matchAll(TOKEN)) {
    if (match.index > last) out.push(text.slice(last, match.index))
    const [token, string, colon, number, literal, bracket, punct] = match
    const key = `t${n++}`
    if (string !== undefined) {
      out.push(
        <span key={key} style={{ color: colon ? 'var(--code-key)' : 'var(--code-string)' }}>
          {string}
        </span>,
      )
      if (colon) out.push(<span key={`${key}c`}>{colon}</span>)
    } else if (number !== undefined) {
      out.push(
        <span key={key} style={{ color: 'var(--code-number)' }}>
          {number}
        </span>,
      )
    } else if (literal !== undefined) {
      out.push(
        <span key={key} style={{ color: 'var(--code-number)' }}>
          {literal}
        </span>,
      )
    } else if (bracket !== undefined) {
      out.push(
        <span key={key} style={{ color: 'var(--code-punct)' }}>
          {bracket}
        </span>,
      )
    } else {
      out.push(punct ?? token)
    }
    last = match.index + token.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Pretty-print when the body is valid JSON; otherwise keep it as written. */
export function formatBody(body) {
  const text = String(body ?? '').trim()
  if (!text) return ''
  try {
    const parsed = JSON.parse(text)
    const compact = JSON.stringify(parsed)
    // Short bodies read best in the skill's own compact layout.
    return compact.length <= 90 ? text : JSON.stringify(parsed, null, 2)
  } catch {
    return text
  }
}

// Shell (cURL) colouring: the command, flags and quoted strings. A body after
// -d '…' keeps its JSON colours.
const SHELL =
  /^(curl)\b|(\s)(-{1,2}[A-Za-z][\w-]*)|'((?:[^'\\]|\\.|'\\'')*)'|("(?:[^"\\]|\\.)*")|^([A-Z]+)(?= )/gm

export function highlightShell(source) {
  const text = String(source ?? '')
  const out = []
  let last = 0
  let n = 0
  for (const match of text.matchAll(SHELL)) {
    if (match.index > last) out.push(text.slice(last, match.index))
    const [token, command, space, flag, single, double, verb] = match
    const key = `s${n++}`
    if (command || verb) {
      out.push(
        <span key={key} style={{ color: 'var(--code-key)' }}>
          {command ?? verb}
        </span>,
      )
    } else if (flag) {
      out.push(space)
      out.push(
        <span key={key} style={{ color: 'var(--code-punct)' }}>
          {flag}
        </span>,
      )
    } else if (single !== undefined) {
      out.push(
        <span key={key}>
          {"'"}
          {highlightJson(single)}
          {"'"}
        </span>,
      )
    } else {
      out.push(
        <span key={key} style={{ color: 'var(--code-string)' }}>
          {double}
        </span>,
      )
    }
    last = match.index + token.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Payloads ("POST path" then JSON) and commands share one entry point. */
export function highlightCode(source) {
  const text = String(source ?? '')
  if (/^curl\b/.test(text)) return highlightShell(text)
  const newline = text.indexOf('\n')
  if (/^[A-Z]+ \S/.test(text) && newline !== -1) {
    return [...highlightShell(text.slice(0, newline + 1)), ...highlightJson(text.slice(newline + 1))]
  }
  return highlightJson(text)
}
